import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { toast } from 'sonner'
import { create } from '@bufbuild/protobuf'
import {
  listFeedback,
  adminUpdateFeedbackStage,
} from '../../gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListFeedbackRequestSchema, ListFeedbackResponseSchema } from '../../gen/cortado/v1/cortado_pb'
import type { ListFeedbackResponse } from '../../gen/cortado/v1/cortado_pb'
import type { Feedback } from '../../gen/cortado/v1/feedback_pb'
import { FeedbackStageEnum, FeedbackTypeEnum } from '../../gen/cortado/v1/settings_pb'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import { Badge } from '../../components/ui/badge'
import { Input } from '../../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover'
import {
  SortableHeader,
  compareNumbers,
  compareStrings,
  useTableSort,
} from '../../components/ui/sortable-header'

export const Route = createFileRoute('/_admin/feedback')({
  component: AdminFeedback,
})

type SortField = 'feedback' | 'author' | 'type' | 'upvotes' | 'stage' | 'created'

const STAGES = [
  { value: FeedbackStageEnum.FEEDBACK_STAGE_NEW, label: 'New' },
  { value: FeedbackStageEnum.FEEDBACK_STAGE_PLANNED, label: 'Planned' },
  { value: FeedbackStageEnum.FEEDBACK_STAGE_IN_PROGRESS, label: 'In Progress' },
  { value: FeedbackStageEnum.FEEDBACK_STAGE_COMPLETE, label: 'Complete' },
  { value: FeedbackStageEnum.FEEDBACK_STAGE_CANCELLED, label: 'Cancelled' },
  { value: FeedbackStageEnum.FEEDBACK_STAGE_WONT_DO, label: "Won't Do" },
]

function getStageBadgeVariant(stage: FeedbackStageEnum) {
  switch (stage) {
    case FeedbackStageEnum.FEEDBACK_STAGE_NEW:
      return 'info' as const
    case FeedbackStageEnum.FEEDBACK_STAGE_PLANNED:
      return 'purple' as const
    case FeedbackStageEnum.FEEDBACK_STAGE_IN_PROGRESS:
      return 'warning' as const
    case FeedbackStageEnum.FEEDBACK_STAGE_COMPLETE:
      return 'success' as const
    case FeedbackStageEnum.FEEDBACK_STAGE_CANCELLED:
      return 'secondary' as const
    case FeedbackStageEnum.FEEDBACK_STAGE_WONT_DO:
      return 'danger' as const
    default:
      return 'outline' as const
  }
}

function getStageLabel(stage: FeedbackStageEnum) {
  return STAGES.find((s) => s.value === stage)?.label ?? 'Unknown'
}

function getTypeLabel(type: FeedbackTypeEnum) {
  switch (type) {
    case FeedbackTypeEnum.FEEDBACK_TYPE_NEW_FEATURE:
      return 'New Feature'
    case FeedbackTypeEnum.FEEDBACK_TYPE_BUG_FIX:
      return 'Bug Fix'
    case FeedbackTypeEnum.FEEDBACK_TYPE_IMPROVEMENT:
      return 'Improvement'
    case FeedbackTypeEnum.FEEDBACK_TYPE_QUESTION:
      return 'Question'
    default:
      return 'Unknown'
  }
}

function getTypeBadgeVariant(type: FeedbackTypeEnum) {
  switch (type) {
    case FeedbackTypeEnum.FEEDBACK_TYPE_NEW_FEATURE:
      return 'default' as const
    case FeedbackTypeEnum.FEEDBACK_TYPE_BUG_FIX:
      return 'danger' as const
    case FeedbackTypeEnum.FEEDBACK_TYPE_IMPROVEMENT:
      return 'success' as const
    case FeedbackTypeEnum.FEEDBACK_TYPE_QUESTION:
      return 'orange' as const
    default:
      return 'outline' as const
  }
}

function decodeHtmlEntities(text: string): string {
  const doc = new DOMParser().parseFromString(text, 'text/html')
  return doc.body.textContent || ''
}

function AdminFeedback() {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [stageFilter, setStageFilter] = useState<string>('all')
  const { sortField, sortDirection, toggleSort } = useTableSort<SortField>('author')
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null)

  const authorName = (f: Feedback) =>
    [f.createdByUser?.firstName, f.createdByUser?.lastName].filter(Boolean).join(' ').trim()

  const { data: feedbackData, isLoading } = useQuery(
    listFeedback,
    create(ListFeedbackRequestSchema, { limit: 100 }),
  )

  const listQueryKey = createConnectQueryKey({
    schema: listFeedback,
    cardinality: undefined,
  })

  const updateStageMutation = useMutation(adminUpdateFeedbackStage, {
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey })

      const previousQueries = queryClient.getQueriesData<ListFeedbackResponse>({
        queryKey: listQueryKey,
      })

      previousQueries.forEach(([queryKey, oldData]) => {
        if (!oldData?.feedback) return

        const updatedFeedback = oldData.feedback.map((f) =>
          f.id === request.id && request.stage !== undefined
            ? { ...f, feedbackStage: request.stage }
            : f,
        )
        queryClient.setQueryData(
          queryKey,
          create(ListFeedbackResponseSchema, {
            feedback: updatedFeedback,
            totalCount: oldData.totalCount,
          }),
        )
      })

      return { previousQueries }
    },
    onSuccess: () => {
      toast.success('Feedback stage updated')
    },
    onError: (err: Error, _request, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      toast.error(`Failed to update stage: ${err.message}`)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
  })

  const filteredFeedback = useMemo(() => {
    let items = feedbackData?.feedback ?? []

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      items = items.filter(
        (f) =>
          f.feedback.toLowerCase().includes(q) ||
          f.createdByUser?.firstName?.toLowerCase().includes(q) ||
          f.createdByUser?.lastName?.toLowerCase().includes(q),
      )
    }

    if (stageFilter !== 'all') {
      const stageNum = Number(stageFilter)
      items = items.filter((f) => f.feedbackStage === stageNum)
    }

    const sorted = [...items].sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1
      switch (sortField) {
        case 'feedback':
          return compareStrings(a.feedback, b.feedback) * dir
        case 'author':
          return compareStrings(authorName(a), authorName(b)) * dir
        case 'type':
          return compareNumbers(a.feedbackType, b.feedbackType) * dir
        case 'upvotes':
          return compareNumbers(a.upvotesCount, b.upvotesCount) * dir
        case 'stage':
          return compareNumbers(a.feedbackStage, b.feedbackStage) * dir
        case 'created':
          return compareNumbers(
            a.createdAt?.seconds ?? 0n,
            b.createdAt?.seconds ?? 0n,
          ) * dir
        default:
          return 0
      }
    })

    return sorted
  }, [feedbackData?.feedback, searchQuery, stageFilter, sortField, sortDirection])

  function Sortable({ field, children }: { field: SortField; children: React.ReactNode }) {
    return (
      <SortableHeader
        active={sortField === field}
        direction={sortDirection}
        onToggle={() => toggleSort(field)}
      >
        {children}
      </SortableHeader>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Feedback</h1>
        <p className="text-muted-foreground text-sm">
          {feedbackData?.totalCount !== undefined
            ? `${feedbackData.totalCount} total feedback items`
            : 'View and manage user feedback'}
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          placeholder="Search feedback..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full sm:max-w-sm"
        />
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map((s) => (
              <SelectItem key={s.value} value={String(s.value)}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]"><Sortable field="feedback">Feedback</Sortable></TableHead>
              <TableHead><Sortable field="author">Author</Sortable></TableHead>
              <TableHead><Sortable field="type">Type</Sortable></TableHead>
              <TableHead><Sortable field="upvotes">Upvotes</Sortable></TableHead>
              <TableHead><Sortable field="stage">Stage</Sortable></TableHead>
              <TableHead><Sortable field="created">Created</Sortable></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Loading feedback...
                </TableCell>
              </TableRow>
            ) : !filteredFeedback.length ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No feedback found
                </TableCell>
              </TableRow>
            ) : (
              filteredFeedback.map((item) => (
                <TableRow key={item.id} className="cursor-pointer" onClick={() => setSelectedFeedback(item)}>
                  <TableCell className="max-w-[300px]">
                    <p className="truncate">
                      {decodeHtmlEntities(item.feedback)}
                    </p>
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">
                    {item.createdByUser?.firstName || item.createdByUser?.lastName
                      ? `${item.createdByUser.firstName ?? ''} ${item.createdByUser.lastName ?? ''}`.trim()
                      : 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getTypeBadgeVariant(item.feedbackType)}>
                      {getTypeLabel(item.feedbackType)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    {item.upvotesCount > 0 ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="cursor-pointer hover:underline text-sm">
                            {item.upvotesCount}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-2" align="center" sideOffset={4}>
                          <p className="text-xs font-medium text-muted-foreground mb-1.5 px-1">Upvoted by</p>
                          <div className="space-y-1">
                            {item.upvoters.map((voter) => (
                              <div key={voter.userId} className="flex items-center gap-1.5 px-1 py-0.5">
                                <span className="text-xs truncate">
                                  {[voter.firstName, voter.lastName].filter(Boolean).join(' ') || 'Unknown'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span className="text-sm">{item.upvotesCount}</span>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={String(item.feedbackStage)}
                      onValueChange={(value) => {
                        const newStage = Number(value) as FeedbackStageEnum
                        if (newStage !== item.feedbackStage) {
                          updateStageMutation.mutate({
                            id: item.id,
                            stage: newStage,
                          })
                        }
                      }}
                    >
                      <SelectTrigger className="w-[150px] h-8">
                        <Badge variant={getStageBadgeVariant(item.feedbackStage)}>
                          {getStageLabel(item.feedbackStage)}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {STAGES.map((s) => (
                          <SelectItem key={s.value} value={String(s.value)}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">
                    {item.createdAt
                      ? new Date(Number(item.createdAt.seconds) * 1000).toLocaleDateString()
                      : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selectedFeedback} onOpenChange={(open) => !open && setSelectedFeedback(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Feedback
              {selectedFeedback && (
                <Badge variant={getTypeBadgeVariant(selectedFeedback.feedbackType)}>
                  {getTypeLabel(selectedFeedback.feedbackType)}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <div className="space-y-4">
              <p className="text-sm whitespace-pre-wrap">{decodeHtmlEntities(selectedFeedback.feedback)}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground border-t pt-3">
                <span>
                  By{' '}
                  {selectedFeedback.createdByUser?.firstName || selectedFeedback.createdByUser?.lastName
                    ? `${selectedFeedback.createdByUser.firstName ?? ''} ${selectedFeedback.createdByUser.lastName ?? ''}`.trim()
                    : 'Unknown'}
                </span>
                {selectedFeedback.upvotesCount > 0 ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="cursor-pointer hover:underline">
                        {selectedFeedback.upvotesCount} upvote{selectedFeedback.upvotesCount !== 1 ? 's' : ''}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2" align="center" sideOffset={4}>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5 px-1">Upvoted by</p>
                      <div className="space-y-1">
                        {selectedFeedback.upvoters.map((voter) => (
                          <div key={voter.userId} className="flex items-center gap-1.5 px-1 py-0.5">
                            <span className="text-xs truncate">
                              {[voter.firstName, voter.lastName].filter(Boolean).join(' ') || 'Unknown'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <span>{selectedFeedback.upvotesCount} upvote{selectedFeedback.upvotesCount !== 1 ? 's' : ''}</span>
                )}
                <Select
                  value={String(selectedFeedback.feedbackStage)}
                  onValueChange={(value) => {
                    const newStage = Number(value) as FeedbackStageEnum
                    if (newStage !== selectedFeedback.feedbackStage) {
                      updateStageMutation.mutate({
                        id: selectedFeedback.id,
                        stage: newStage,
                      })
                      setSelectedFeedback({ ...selectedFeedback, feedbackStage: newStage })
                    }
                  }}
                >
                  <SelectTrigger className="h-7 w-auto gap-1 border-none px-0 shadow-none">
                    <Badge variant={getStageBadgeVariant(selectedFeedback.feedbackStage)}>
                      {getStageLabel(selectedFeedback.feedbackStage)}
                    </Badge>
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => (
                      <SelectItem key={s.value} value={String(s.value)}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>
                  {selectedFeedback.createdAt
                    ? new Date(Number(selectedFeedback.createdAt.seconds) * 1000).toLocaleDateString()
                    : '-'}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
